<template>
  <!--
    Шапка это thead внутри той же таблицы, что и строки (смотри QTLiteBody.vue).
    Отдельной таблицы под шапку больше нет, поэтому колонки шапки и строк всегда
    совпадают сами собой. В режиме sticky шапка прилипает к верху при прокрутке, в
    остальных режимах уезжает вместе с содержимым.
  -->
  <thead class="qtl-thead" :class="{ 'qtl-thead--sticky': headerMode === 'sticky' }">
    <tr>
      <th
        v-for="col in columns"
        :key="col.id"
        :data-col-id="col.id"
        :class="thClasses(col)"
        @mouseenter="onThEnter(col, $event)"
        @mouseleave="onThLeave"
      >
        <!--
          Первая колонка с чекбоксами: чекбокс выбрать все или снять, и карандашик.
          Обычная кнопка с такой же картинкой, без тяжёлых компонентов.
          Меню действий открывает родитель.
        -->
        <template v-if="col.isMultiedit">
          <div class="qtl-th__multi-row">
            <mosta-checkbox
              v-if="WA"
              class="qtl-th__check"
              :model-value="headerCheckbox"
              :true-value="'all'"
              :false-value="'none'"
              @update:model-value="$emit('headerCheckboxChange', $event)"
              @click.stop
            />
            <button
              class="qtl-th__edit-btn"
              type="button"
              :disabled="!WA || !multieditAllowed"
              :aria-label="t('multi_edit')"
              @click.stop="$emit('multieditClick', $event)"
            >
              <!-- картинка карандашика прямо тут, без отдельного компонента иконки -->
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M14.9372 3.83936C15.1701 3.60633 15.1203 3.1787 14.8259 2.8843L13.1148 1.17324C12.8203 0.878802 12.3927 0.828861 12.1597 1.06191L10.7524 2.46913L13.5299 5.24659L14.9372 3.83936Z" fill="currentColor" stroke="currentColor" stroke-width="0.390625"/>
                <path d="M1.00066 15.195C1.64288 15.0543 3.58866 14.546 4.27003 14.3671C4.42665 14.3261 4.56959 14.2439 4.68397 14.1292L12.8352 5.97791L10.0576 3.20032L1.90638 11.3516C1.79163 11.4659 1.7096 11.6089 1.66846 11.7656C1.4895 12.4472 0.981335 14.3934 0.841554 15.0363C0.831666 15.0807 0.845216 15.1271 0.877441 15.1592C0.909666 15.1914 0.956223 15.2048 1.00066 15.195Z" fill="currentColor" stroke="currentColor" stroke-width="0.390625"/>
              </svg>
            </button>
          </div>
            <button
              v-if="showGlobalPinnedSort"
              class="qtl-th__global-sort-btn"
              type="button"
              :aria-label="t('sort_by_pinned')"
              :disabled="blocked"
              @click.stop="onPinnedSortClick"
            >
            <!--
              Та же двухстрелочная иконка, что и у колонок. Сортировка по закреплённым
              включена -> зелёной становится стрелка вниз (левая), иначе обе серые.
            -->
            <svg
              class="qtl-th__sort-svg"
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g :class="orderByPinned ? 'qtl-sort-arrow--active' : 'qtl-sort-arrow--idle'">
                <path d="M2 10.6666L4.66667 13.3333L7.33333 10.6666" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M4.66669 13.3333V2.66663" stroke-linecap="round" stroke-linejoin="round"/>
              </g>
              <g class="qtl-sort-arrow--idle">
                <path d="M14 5.33329L11.3334 2.66663L8.66669 5.33329" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M11.3333 2.66663V13.3333" stroke-linecap="round" stroke-linejoin="round"/>
              </g>
            </svg>
          </button>
        </template>
        <template v-else>
          <!--
            Шапка колонки: значок сортировки и название идут одной строкой (inline).
            Значок (если по колонке можно сортировать) стоит перед текстом, текст
            обтекает и переносится, длинные слова можно ломать. Вид значка зависит от
            типа данных колонки (символьный/числовой/логический/двухстрелочный) —
            подробности в QTLiteSortIcon. Активное направление зелёное, без
            сортировки — серое.
          -->
          <div class="qtl-th__col" v-lead-clamp="{ id: col.id, text: col.text }">
            <button
              v-if="col.sort"
              class="qtl-th__sort-btn"
              type="button"
              aria-label="Sort"
              :disabled="blocked"
              @click.stop="onHeaderClick(col)"
            >
              <!--
                Значок сортировки, вид зависит от типа данных колонки (см. QTLiteSortIcon).
                Активное направление подсвечивается зелёным, отсутствие сортировки — серым.
              -->
              <qt-lite-sort-icon :data-type="col.dataType" :order="col.order" />
              <q-tooltip v-if="!blocked" :delay="300" anchor="top middle" self="center middle" class="qtl-sort-tooltip">
                {{ sortTip(col) }}
              </q-tooltip>
            </button><span class="qtl-th__text"></span>
          </div>
        </template>
        <!--
          Зоны для растягивания колонок рисует отдельный QTLiteResizeOverlay
          (смотри QueryTableLite). Это тонкие прозрачные полоски на всю высоту
          таблицы, при наведении меняется курсор.
        -->
      </th>
    </tr>
  </thead>

  <!--
    Подсказка для обрезанных названий: всплывает по наведению с задержкой и
    показывает ту же ячейку (та же кнопка сортировки, та же ширина и позиция по
    верху/бокам), но без ограничения по высоте и с полным текстом. Висит в body
    через teleport, чтобы её не резал скролл-контейнер и sticky-шапка.
  -->
  <teleport to="body">
    <transition name="qtl-th__overlay-fade">
    <div
      v-if="overlayCol"
      class="qtl-th__overlay"
      :style="overlayStyle"
      @mouseleave="hideOverlay"
    >
      <div class="qtl-th__col qtl-th__col--overlay">
        <button
          v-if="overlayCol.sort"
          class="qtl-th__sort-btn"
          type="button"
          aria-label="Sort"
          :disabled="blocked"
          @click.stop="onHeaderClick(overlayCol)"
        >
          <qt-lite-sort-icon :data-type="overlayCol.dataType" :order="overlayCol.order" />
          <q-tooltip v-if="!blocked" :delay="300" anchor="top middle" self="center middle" class="qtl-sort-tooltip">
            {{ sortTip(overlayCol) }}
          </q-tooltip>
        </button><span class="qtl-th__text" v-html="overlayCol.text"></span>
      </div>
    </div>
    </transition>
  </teleport>
</template>

<script setup lang="ts">
import MostaCheckbox from "@/components/common/basic/MostaCheckbox.vue";
import QtLiteSortIcon from "./QTLiteSortIcon.vue";
import { i18n } from "@/i18n";
import { computed, onBeforeUnmount, onMounted, reactive, ref } from "vue";
import type { Directive } from "vue";
import { ColumnCategory, TableHeaderCellDataType } from "@/models/etc/types";
import type { LiteColumn } from "../types";

const t = (key: string) => i18n.global.t(key);

const DT = TableHeaderCellDataType;

// Следующее состояние сортировки по клику. Порядок зависит от типа колонки:
//  • числовые: по умолчанию (1) → убыв. (3) → возр. (2) → по умолчанию (1)
//    (первым нажатием показываем от большего к меньшему);
//  • остальные типы: по умолчанию (1) → возр. (2) → убыв. (3) → по умолчанию (1).
function nextOrderOf(col: LiteColumn): number {
  const isNum = col.dataType === DT.Number;
  // из дефолта (order не 2 и не 3): числовые стартуют с убывания, остальные — с возрастания
  if (col.order !== 2 && col.order !== 3) return isNum ? 3 : 2;
  if (isNum) return col.order === 3 ? 2 : 1;
  return col.order === 2 ? 3 : 1;
}

/*
 Подсказка на значке сортировки описывает состояние, в которое перейдёт колонка ПОСЛЕ
 клика (следующий order), а не текущее. Возврат к дефолту (next === 1) одинаков для всех
 типов. Для остальных текст зависит от типа данных колонки; у даты значок такой же, как у
 логического поля, а текст подсказки отдельный.
*/
function sortTip(col: LiteColumn): string {
  const next = nextOrderOf(col);
  if (next === 1) return t("sort_tip_default");
  const asc = next === 2;
  // Колонка этапов проекта: сортировка описывается как переход от начального этапа
  // к конечному и наоборот, независимо от типа данных.
  if (col.sortCategory === ColumnCategory.Stage) {
    return t(asc ? "sort_tip_stage_asc" : "sort_tip_stage_desc");
  }
  switch (col.dataType) {
    case DT.Number: return t(asc ? "sort_tip_num_asc" : "sort_tip_num_desc");
    case DT.String: return t(asc ? "sort_tip_str_asc" : "sort_tip_str_desc");
    case DT.Boolean: return t(asc ? "sort_tip_bool_asc" : "sort_tip_bool_desc");
    case DT.DateTime: return t(asc ? "sort_tip_date_asc" : "sort_tip_date_desc");
    default: return t(asc ? "sort_tip_gen_asc" : "sort_tip_gen_desc");
  }
}

// Название колонки зажимается в 3 строки, многоточие ставится В НАЧАЛЕ — так видна
// концовка названия, а не его начало.
// Чистым CSS так нельзя: line-clamp умеет «…» только справа, а трюк с direction: rtl
// разворачивает слова. Поэтому замер и обрезка выполняются вручную.
// Название хранится как HTML (бывает <br/>), поэтому режутся не строка, а DOM-узлы —
// иначе порвётся разметка. Полный текст лежит в WeakMap, в самом узле он уже обрезан.
// Высота меряется по контейнеру .qtl-th__col (он блочный), у inline-текста box-метрики
// врут. Первая строка может быть выше из-за значка сортировки, поэтому её высота не
// прикидывается, а замеряется отдельно.
const LEAD_CLAMP_MAX_LINES = 3;
const fullTextHtml = new WeakMap<HTMLElement, string>();
const clampObservers = new WeakMap<HTMLElement, ResizeObserver>();
const clampLastWidth = new WeakMap<HTMLElement, number>();
const clampColId = new WeakMap<HTMLElement, string>();
// какие колонки сейчас реально обрезаны — для них при наведении доступна подсказка
const truncatedCols = reactive<Record<string, boolean>>({});

function removeFirstLeadingChar(textEl: HTMLElement, ellipsis: Text): boolean {
  let node: Node | null = ellipsis.nextSibling;
  while (node) {
    if (node.nodeType === Node.TEXT_NODE) {
      const tn = node as Text;
      if (tn.data.length > 0) {
        tn.deleteData(0, 1);
        if (tn.data.length === 0) textEl.removeChild(tn);
        return true;
      }
      const empty = node;
      node = node.nextSibling;
      textEl.removeChild(empty);
      continue;
    }
    // в начале целый элемент (тот же <br/>) — удаляется целиком
    textEl.removeChild(node);
    return true;
  }
  return false;
}

function applyLeadClamp(colEl: HTMLElement) {
  const textEl = colEl.querySelector<HTMLElement>(".qtl-th__text");
  if (!textEl) return;

  const lh = parseFloat(getComputedStyle(textEl).lineHeight) || 16;
  // пол-строки запаса на субпиксели: без него третья строка иногда «не влезает» на
  // доли пикселя и текст схлопывается до двух. Меньше полстроки — значит честная
  // четвёртая строка (+lh) за лимит всё равно вылезет и обрежется.
  const tol = Math.floor(lh / 2);

  // лимит сбрасывается, чтобы измерить настоящую высоту
  colEl.style.overflow = "";
  colEl.style.maxHeight = "";

  // высота одной строки: в текст ставится один символ и читается scrollHeight. В него
  // уже зашиты и паддинги колонки, и значок сортировки на первой строке — без догадок
  textEl.innerHTML = "\u00A0";
  const oneLine = colEl.scrollHeight;
  const cap = oneLine + Math.round(lh * (LEAD_CLAMP_MAX_LINES - 1)) + tol;

  // полный текст возвращается, высота прижимается к трём строкам
  textEl.innerHTML = fullTextHtml.get(colEl) ?? "";
  colEl.style.overflow = "hidden";
  colEl.style.maxHeight = `${cap}px`;

  const id = clampColId.get(colEl);

  // всё и так в трёх строках — резать нечего
  if (colEl.scrollHeight <= cap) {
    if (id) truncatedCols[id] = false;
    return;
  }

  const ellipsis = document.createTextNode("…");
  textEl.insertBefore(ellipsis, textEl.firstChild);

  // символы откусываются с начала, пока хвост с «…» не влезет. guard на случай, если
  // из-за округлений условие вдруг не сойдётся — чтобы цикл не завис
  let guard = 100000;
  while (colEl.scrollHeight > cap && guard-- > 0) {
    if (!removeFirstLeadingChar(textEl, ellipsis)) break;
  }

  if (id) truncatedCols[id] = true;
}

type LeadClampValue = { id: string; text: string };

const vLeadClamp: Directive<HTMLElement, LeadClampValue> = {
  mounted(el, binding) {
    fullTextHtml.set(el, binding.value?.text ?? "");
    clampColId.set(el, binding.value?.id ?? "");
    clampLastWidth.set(el, Math.round(el.clientWidth));
    applyLeadClamp(el);
    // отслеживается только ширина: от высоты пересчёт не зависит, потому что
    // applyLeadClamp сам временно меняет высоту контейнера — иначе были бы лишние
    // срабатывания и петля
    const ro = new ResizeObserver((entries) => {
      const w = Math.round(entries[0]?.contentRect.width ?? el.clientWidth);
      if (clampLastWidth.get(el) === w) return;
      clampLastWidth.set(el, w);
      applyLeadClamp(el);
    });
    ro.observe(el);
    clampObservers.set(el, ro);
  },
  updated(el, binding) {
    const textChanged = binding.value?.text !== binding.oldValue?.text;
    if (textChanged) fullTextHtml.set(el, binding.value?.text ?? "");
    clampColId.set(el, binding.value?.id ?? "");
    const w = Math.round(el.clientWidth);
    // ни текст, ни ширина не изменились — пересчёт не нужен. За шириной всё равно
    // следит ResizeObserver, поэтому посторонние ререндеры компонента пропускаются
    if (!textChanged && clampLastWidth.get(el) === w) return;
    clampLastWidth.set(el, w);
    applyLeadClamp(el);
  },
  beforeUnmount(el) {
    clampObservers.get(el)?.disconnect();
    clampObservers.delete(el);
    fullTextHtml.delete(el);
    clampLastWidth.delete(el);
    const id = clampColId.get(el);
    if (id) delete truncatedCols[id];
    clampColId.delete(el);
  },
};

const props = defineProps<{
  columns: LiteColumn[];
  /** как ведёт себя шапка: sticky (прилипает), scroll или page (уезжает) */
  headerMode?: "sticky" | "scroll" | "page";
  /** состояние верхнего чекбокса: none, all или many */
  headerCheckbox: string;
  /** есть ли права на правку, нужно чтобы гасить карандашик в первой колонке */
  WA?: boolean;
  /** можно ли вообще править записи у этой сущности */
  multieditAllowed?: boolean;
  /** сейчас сортируем по закреплённым, для значка в первой колонке */
  orderByPinned?: boolean;
  /** показывать ли значок этой общей сортировки в первой колонке */
  showGlobalPinnedSort?: boolean;
  /** идёт обновление: тело таблицы неинтерактивно, сортировку в шапке тоже не даём нажимать */
  blocked?: boolean;
}>();

const emit = defineEmits<{
  (e: "headerCheckboxChange", value: string): void;
  (e: "sort", payload: { col: LiteColumn; nextOrder: number }): void;
  (e: "multieditClick", ev: MouseEvent): void;
  (e: "togglePinnedOrder"): void;
}>();

function onHeaderClick(col: LiteColumn) {
  // во время обновления таблица заблокирована — сортировку не переключаем
  if (props.blocked) return;
  if (!col.sort) return;
  // порядок цикла зависит от типа колонки (см. nextOrderOf): числовые начинают
  // с убывания, остальные — с возрастания
  const next = nextOrderOf(col);
  emit("sort", { col, nextOrder: next });
}

// общая сортировка по закреплённым — тоже блокируется во время обновления
function onPinnedSortClick() {
  if (props.blocked) return;
  emit("togglePinnedOrder");
}

/*
 Классы для ячейки шапки. Запоминаем, по какой колонке сейчас сортируем,
 чтобы можно было слегка подсветить её фон, как делали раньше.
*/
function thClasses(col: LiteColumn) {
  const sorting = col.order === 2 ? "asc" : col.order === 3 ? "desc" : "none";
  return {
    "qtl-th": true,
    "qtl-th--multiedit": !!col.isMultiedit,
    "qtl-th--sortable": col.sort,
    "qtl-th--sort-active": col.sort && (sorting === "asc" || sorting === "desc"),
  };
}

/*
 Подсказка для обрезанных названий. Появляется через 300мс после наведения на
 ячейку, но только если эта колонка действительно обрезана (truncatedCols). Позиция и
 ширина берутся с самой ячейки, поэтому подсказка ложится ровно поверх неё по верху и
 бокам, а вниз растёт сколько надо.
*/
// колонку в подсказке держим по id и берём живую из props.columns — тогда после
// клика по кнопке сортировки в оверлее стрелка обновляется так же, как в самой шапке
const overlayColId = ref<string | null>(null);
const overlayCol = computed<LiteColumn | null>(
  () => props.columns.find((c) => c.id === overlayColId.value) ?? null,
);
const overlayRect = ref<{ left: number; top: number; width: number } | null>(null);
let showTimer: ReturnType<typeof setTimeout> | undefined;

const overlayStyle = computed(() => {
  const r = overlayRect.value;
  if (!r) return "";
  // на полпикселя левее и на пиксель выше, ширина как у ячейки — чтобы подсказка
  // вставала по границам
  return `left:${r.left - 0.5}px; top:${r.top - 1}px; width:${r.width}px;`;
});

function clearShowTimer() {
  if (showTimer !== undefined) {
    clearTimeout(showTimer);
    showTimer = undefined;
  }
}

function onThEnter(col: LiteColumn, ev: MouseEvent) {
  if (col.isMultiedit || !truncatedCols[col.id]) return;
  const thEl = ev.currentTarget as HTMLElement;
  clearShowTimer();
  showTimer = setTimeout(() => {
    const r = thEl.getBoundingClientRect();
    overlayRect.value = { left: r.left, top: r.top, width: r.width };
    overlayColId.value = col.id;
  }, 300);
}

function onThLeave() {
  // пока подсказка не показалась — её появление отменяется. Если уже показалась,
  // действий нет: курсор ушёл на саму подсказку (она лежит сверху), и она прячется по
  // собственному mouseleave
  clearShowTimer();
}

function hideOverlay() {
  clearShowTimer();
  overlayColId.value = null;
  overlayRect.value = null;
}

// при прокрутке координаты ячейки устаревают, поэтому подсказка убирается
onMounted(() => window.addEventListener("scroll", hideOverlay, true));
onBeforeUnmount(() => {
  window.removeEventListener("scroll", hideOverlay, true);
  clearShowTimer();
});
</script>

<style lang="scss" scoped>
/*
  Вид шапки делаем такой же, как был у старой таблицы: сверху значок сортировки
  (он виден, если по колонке можно сортировать), снизу название по центру, длинные
  слова переносятся. Размеры шрифта и линии те же, что и были: отступы 1px,
  шрифт 13px, тонкая серая линия снизу и справа.
*/

.qtl-thead {
  background: #fff;
}

/*
  В режиме sticky шапка прилипает к верху при прокрутке вниз, а вбок едет вместе
  со строками, потому что таблица и прокрутка одни. За счёт этого шапка отделена
  от содержимого, как было в старой таблице. В остальных режимах класса нет и
  шапка уезжает вместе с содержимым.
*/
.qtl-thead--sticky .qtl-th {
  position: sticky;
  top: 0;
  z-index: 3;
}

.qtl-th {
  padding: 1px;
  /* Общий минимум ширины колонки 50px (кроме колонки с галками — у неё свой
     фиксированный размер ниже). Нужен, потому что текст ломается по буквам и без
     минимума колонка могла бы ужаться до пары символов. */
  min-width: 50px;
  text-align: center;
  vertical-align: top;
  /* Title/H3_medium */
  font-family: Roboto;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 16px;
  color: #333333;
  background: #fff;
  border-right: 1px solid #D2D2D2;
  /*
    Обычная нижняя граница у прилипшей шапки иногда уезжает при прокрутке,
    поэтому линию снизу рисуем тенью внутрь, так она никуда не девается.
  */
  box-shadow: inset 0 -1px 0 #D2D2D2;
  white-space: normal;
  /*
    Разрешаем перенос длинных названий колонок по буквам, а не только по
    пробелам, чтобы узкие колонки не распирало широким словом.
  */
  word-break: break-word;
  overflow-wrap: anywhere;
  user-select: none;
}

.qtl-th:last-of-type {
  border-right: none;
}

.qtl-th--multiedit {
  width: 55px;
  min-width: 55px;
  text-align: left;
  vertical-align: top;
}

/* Колонка, по которой сейчас сортируем, чуть подсвечена, как раньше. */
.qtl-th--sort-active {
  background: #fafafa;
}

/* Первая колонка: чекбокс и карандашик в ряд, прижаты к левому верхнему углу. */
.qtl-th__multi-row {
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 2px;
  padding: 4px;
}

.qtl-th__check {
  margin: 2px;
}

.qtl-th__global-sort-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  margin-left: 24px;
  margin-top: 1px;
  padding: 0;
  border: 0;
  border-radius: 4px;
  background: transparent;
  color: #3F884F;
  cursor: pointer;
}

.qtl-th__global-sort-btn:hover {
  background: rgba(63, 136, 79, 0.08);
}

/* Обычная кнопка 22 на 22, прозрачный фон, серый значок, при наведении зелёный. */
.qtl-th__edit-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  padding: 0;
  margin: 0;
  background: transparent;
  border: 0;
  border-radius: 50%;
  color: #979797;
  cursor: pointer;
}

.qtl-th__edit-btn:hover:not(:disabled) {
  color: #3F884F;
  background: rgba(63, 136, 79, 0.08);
}

.qtl-th__edit-btn:disabled {
  color: #c8c8c8;
  cursor: default;
}

/* Содержимое шапки колонки: значок сортировки и название одной строкой (inline).
   Текст обтекает значок и переносится, длинные слова разрешено ломать. */
.qtl-th__col {
  display: block;
  padding: 3px;
  text-align: center;
  word-break: break-word;
  overflow-wrap: anywhere;
}

.qtl-th__sort-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  /* выравниваем значок по середине строки текста */
  vertical-align: middle;
  width: 18px;
  height: 18px;
  padding: 0;
  margin: 0 2px 0 0;
  border: 0;
  border-radius: 4px;
  background: transparent;
  cursor: pointer;
}

.qtl-th__sort-btn:hover {
  background: rgba(63, 136, 79, 0.08);
}

/* во время обновления сортировка недоступна: убираем hover и курсор-указатель */
.qtl-th__sort-btn:disabled {
  cursor: default;
}

.qtl-th__sort-btn:disabled:hover {
  background: transparent;
}

.qtl-th__global-sort-btn:disabled {
  cursor: default;
}

.qtl-th__global-sort-btn:disabled:hover {
  background: transparent;
}

.qtl-th__sort-svg {
  display: block;
  flex-shrink: 0;
}

/* Цвет стрелок сортировки: активная (текущее направление) зелёная, неактивная серая.
   stroke наследуется от группы к её path. */
.qtl-sort-arrow--active {
  stroke: #3F884F;
}

.qtl-sort-arrow--idle {
  stroke: #979797;
}

.qtl-th__text {
  /* inline, чтобы текст шёл сразу за значком и переносился по строкам.
     Высоту шапки не ограничиваем: длинный текст разъезжается вниз сколько нужно,
     за динамической высотой thead следит ResizeObserver. */
  display: inline;
  vertical-align: middle;
  /* перенос по любому символу, а не только по пробелам: длинные слова режутся,
     чтобы текст укладывался в ширину узкой колонки */
  word-break: break-all;
  overflow-wrap: anywhere;
  /* Title/H3_medium */
  color: #333333;
  font-family: Roboto;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 16px;
}

/* Всплывающая подсказка с полным названием обрезанной колонки. Лежит в body,
   позиционируется по координатам ячейки. Паддинг 1px такой же, как у .qtl-th,
   чтобы внутренний .qtl-th__col встал ровно туда же, где он в самой ячейке. */
.qtl-th__overlay {
  position: fixed;
  /* выше sticky-шапки (z-index 3) и поверх диалогов Quasar (~6000), чтобы подсказка
     была видна и в обычной таблице, и когда таблица открыта в диалоге */
  z-index: 6500;
  box-sizing: border-box;
  padding: 1px;
  background: #FFF;
  border-radius: 0 0 8px 8px;
  box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.20);
}

/* В подсказке высота не ограничивается — виден весь текст (max-height/overflow тут
   никто не ставит, в отличие от ячеек с директивой обрезки). */
.qtl-th__col--overlay {
  max-height: none;
  overflow: visible;
}

/* Плавное появление подсказки по прозрачности. */
.qtl-th__overlay-fade-enter-active {
  transition: opacity 0.2s ease;
}

.qtl-th__overlay-fade-leave-active {
  transition: opacity 0.1s ease;
}

.qtl-th__overlay-fade-enter-from,
.qtl-th__overlay-fade-leave-to {
  opacity: 0;
}
</style>

<!-- Тултипы сортировки. q-tooltip телепортируется в body, поэтому стиль не может
     быть scoped. Повторяем тёмный тултип из NativeInput (field_cannot_be_empty):
     фон rgba(92,92,92,0.90), белый текст, скругление 8px. -->
<style lang="scss">
.q-tooltip.qtl-sort-tooltip {
  border-radius: 8px;
  background: var(--color-tooltip, rgba(92, 92, 92, 0.90));
  box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.20);
  padding: 2px 8px;
  color: var(--Text-color-text-btn, #FFF);
  font-family: Roboto, sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
}
</style>
